import type {
  HotelBookableOption,
  HotelBookableOptionAlternativeRoom,
  HotelBookableOptionWithAlternativeRoom,
  HotelBookableOptionWithPax,
  HotelBookableRoom,
  HotelIngredientAdapter,
  HotelIngredientResponse,
  Pax,
  SimplifiedPax,
  RoomPax,
  UnavailableHotelIngredientResponse,
  HotelAlternativeOptionWithBasicRooms,
} from '@libs/trip-planner-interfaces';
import { IngredientType } from '@libs/trip-planner-interfaces';
import { assertValue, isDefined } from '@libs/utils';

import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';

import type { IngredientResponseQL } from './itineraryResponseQL';
import { toMoney } from './moneyResponseQL';
import { toActivityIngredientResponse } from './activityIngredientResponseQL';
import {
  toHotelBookableOption,
  toHotelBookableRoom,
} from './hotelBookableOptionResponseQL';

export type HotelIngredientResponseQL = Extract<
  IngredientResponseQL,
  { type: 'HotelIngredientResponseQL' }
>;
export function toHotelIngredientResponse(
  hotelIngredientResponseQL: HotelIngredientResponseQL,
  hotelIngredientAdapter: HotelIngredientAdapter
): HotelIngredientResponse {
  const suggested = isDefined(hotelIngredientResponseQL.suggested)
    ? getSuggestedFromResponse(hotelIngredientResponseQL.suggested)
    : null;

  const unavailableHotelIngredientResponse =
    toUnavailableHotelIngredientResponse(
      hotelIngredientResponseQL,
      hotelIngredientAdapter
    );

  if (suggested) {
    const maxNumberOfSuggestions = 10;
    return {
      ...unavailableHotelIngredientResponse,
      isAvailable: true,
      suggested: suggested.withPax,
      suggestions: hotelIngredientResponseQL.suggestions
        ? hotelIngredientResponseQL.suggestions
            .slice(0, maxNumberOfSuggestions)
            .map(toHotelSuggestion)
        : [],
      options: [suggested.basic],
      addToBasketToken: suggested.basic.addToBasketToken,
      alternativeSuggestion: null,
    };
  } else {
    return unavailableHotelIngredientResponse;
  }
}

export type HotelIngredientResponseSuggestedQL = NonNullable<
  HotelIngredientResponseQL['suggested']
>;
function getSuggestedFromResponse(
  responseSuggested: HotelIngredientResponseSuggestedQL
): {
  basic: HotelBookableOption;
  withPax: HotelBookableOptionWithPax | HotelBookableOptionWithAlternativeRoom;
} {
  const hotelBookableOption = toHotelBookableOption(responseSuggested);

  assertSmartSearchList(responseSuggested.rooms);
  const rooms = responseSuggested.rooms.map(toHotelBookableRoom);

  return {
    basic: hotelBookableOption,
    withPax: {
      ...hotelBookableOption,
      pax: getTotalPax(rooms),
      rooms: rooms.map((roomObject) => ({
        description: roomObject.description,
        cancellationPolicies: roomObject.cancellationPolicies,
        taxesAndFees: roomObject.taxesAndFees,
        price: roomObject.price,
        boardBasis: roomObject.boardBasis,
      })),
    },
  };
}

function getTotalPax(rooms: HotelBookableRoom[]): Pax {
  return rooms.reduce(
    (
      totalPax: Pax,
      room: HotelBookableOptionAlternativeRoom | HotelBookableRoom
    ) => {
      const roomPax: RoomPax = room.pax.reduce(
        (roomPax: RoomPax, currentRoomPax: SimplifiedPax) => {
          if (currentRoomPax.age === null) {
            roomPax.numberOfAdults++;
          } else {
            roomPax.childAges
              ? roomPax.childAges.push(currentRoomPax.age)
              : (roomPax.childAges = [currentRoomPax.age]);
          }
          return roomPax;
        },
        { numberOfAdults: 0 }
      );
      totalPax.push(roomPax);
      return totalPax;
    },
    []
  );
}

type SuggestionsQL = NonNullable<HotelIngredientResponseQL['suggestions']>;
type SuggestionQL = NonNullable<SuggestionsQL[number]>;
function toHotelSuggestion(
  suggestionQL: SuggestionQL
): HotelAlternativeOptionWithBasicRooms {
  assertSmartSearchValue(suggestionQL.prices?.deltaPrice);
  assertSmartSearchValue(suggestionQL.prices?.totalPrice);
  assertSmartSearchList(suggestionQL.rooms);

  const deltaPrice = toMoney(suggestionQL.prices.deltaPrice);
  const totalPrice = toMoney(suggestionQL.prices.totalPrice);
  return {
    addToBasketToken: suggestionQL.addToBasketToken,
    code: suggestionQL.code,
    isRefundable: suggestionQL.isRefundable,
    prices: {
      deltaPrice,
      totalPrice,
    },
    rooms: suggestionQL.rooms.map(toHotelBookableRoom),
    totalPrice,
  };
}

function toUnavailableHotelIngredientResponse(
  result: HotelIngredientResponseQL,
  ingredient: HotelIngredientAdapter
): UnavailableHotelIngredientResponse {
  assertSmartSearchValue(result.date);
  assertSmartSearchValue(result.lengthOfStay);

  return {
    suggested: null,
    suggestions: null,
    options: [],
    addToBasketToken: null,
    date: new Date(result.date),
    numberOfNights: result.lengthOfStay.numberOfNights,
    isNumberOfNightsSuggested: result.lengthOfStay.nightsHaveBeenSuggested,
    ingredientType: IngredientType.Hotel,
    ingredientId: ingredient.ingredientId,
    subIngredientResponse:
      result.subIngredientResults?.map((subResult, subIndex) => {
        assertSmartSearchValue(subResult);

        const subIngredient = ingredient.subIngredients[subIndex];

        assertValue(
          subIngredient,
          'search-service-smart-search > mappers.ts > toHotelIngredientResponse > subIngredient'
        );

        if (subResult.type === 'TicketIngredientResponseQL') {
          return toActivityIngredientResponse(subResult, subIngredient);
        } else {
          throw new Error('Unhandled subIngredient type');
        }
      }) ?? [],
    isAvailable: false,
    alternativeSuggestion: result.alternativeSuggestion
      ? toAlternativeSuggestion(result.alternativeSuggestion)
      : null,
  };
}

type AlternativeSuggestionQL = NonNullable<
  HotelIngredientResponseQL['alternativeSuggestion']
>;
function toAlternativeSuggestion(
  alternativeSuggestionQL: AlternativeSuggestionQL
): HotelBookableOptionWithAlternativeRoom {
  assertSmartSearchList(alternativeSuggestionQL.rooms);
  assertSmartSearchValue(alternativeSuggestionQL.totalPrice);

  const rooms = alternativeSuggestionQL.rooms.map(
    toHotelBookableOptionAlternativeRoom
  );

  return {
    addToBasketToken: alternativeSuggestionQL.addToBasketToken,
    code: alternativeSuggestionQL.code,
    isRefundable: alternativeSuggestionQL.isRefundable,
    rooms,
    totalPrice: toMoney(alternativeSuggestionQL.totalPrice),
    pax: getTotalPax(rooms),
  };
}

type AlternativeSuggestionRoomsQL = NonNullable<
  AlternativeSuggestionQL['rooms']
>;
type AlternativeSuggestionRoomQL = NonNullable<
  AlternativeSuggestionRoomsQL[number]
>;
function toHotelBookableOptionAlternativeRoom(
  alternativeSuggestionRoomQL: AlternativeSuggestionRoomQL
): HotelBookableOptionAlternativeRoom {
  return {
    ...toHotelBookableRoom(alternativeSuggestionRoomQL),
    roomCode: alternativeSuggestionRoomQL.roomCode,
  };
}
